const SIGNUP_FORM_KEY = 'qflow_signup_form_data';

// Signup flow step constants
export const SIGNUP_STEPS = {
    INITIAL_FORM: 'initial-form',
    PASSWORD_SETUP: 'password-setup', 
    CODE_VERIFICATION: 'code-verification',
    COMPLETE: 'complete'
};

export const signupFormStorage = {
    save: (formData) => {
        try {
            const existingData = signupFormStorage.retrieve() || {};
            const dataToStore = {
                ...existingData,
                ...formData,
                timestamp: Date.now()
            };
            sessionStorage.setItem(SIGNUP_FORM_KEY, JSON.stringify(dataToStore));
            return true;
        } catch (error) {
            console.warn('Failed to save signup form data to sessionStorage:', error);
            return false;
        }
    },

    retrieve: () => {
        try {
            const storedData = sessionStorage.getItem(SIGNUP_FORM_KEY);
            if (!storedData) {
                return null;
            }

            const parsedData = JSON.parse(storedData);
            
            // Check if data is stale (older than 24 hours)
            const isStale = parsedData.timestamp && (Date.now() - parsedData.timestamp > 24 * 60 * 60 * 1000);
            if (isStale) {
                signupFormStorage.clear();
                return null;
            }

            // Remove timestamp before returning
            const { timestamp, ...formData } = parsedData;
            return formData;
        } catch (error) {
            console.warn('Failed to retrieve signup form data from sessionStorage:', error);
            signupFormStorage.clear(); // Clear corrupted data
            return null;
        }
    },

    clear: () => {
        try {
            sessionStorage.removeItem(SIGNUP_FORM_KEY);
            return true;
        } catch (error) {
            console.warn('Failed to clear signup form data from sessionStorage:', error);
            return false;
        }
    },

    exists: () => {
        try {
            return sessionStorage.getItem(SIGNUP_FORM_KEY) !== null;
        } catch (error) {
            return false;
        }
    },

    update: (partialData) => {
        const existingData = signupFormStorage.retrieve();
        if (existingData) {
            const updatedData = { ...existingData, ...partialData };
            return signupFormStorage.save(updatedData);
        }
        return signupFormStorage.save(partialData);
    },

    // Signup state management methods
    markSignupSubmitted: () => {
        return signupFormStorage.update({
            signupSubmitted: true,
            submissionTimestamp: Date.now(),
            currentStep: SIGNUP_STEPS.PASSWORD_SETUP
        });
    },

    setCurrentStep: (step) => {
        if (!Object.values(SIGNUP_STEPS).includes(step)) {
            console.warn('Invalid signup step:', step);
            return false;
        }
        return signupFormStorage.update({ currentStep: step });
    },

    getCurrentStep: () => {
        const data = signupFormStorage.retrieve();
        return data?.currentStep || SIGNUP_STEPS.INITIAL_FORM;
    },

    isSignupSubmitted: () => {
        const data = signupFormStorage.retrieve();
        return data?.signupSubmitted === true;
    },

    getSubmissionTimestamp: () => {
        const data = signupFormStorage.retrieve();
        return data?.submissionTimestamp || null;
    },

    markComplete: () => {
        return signupFormStorage.update({
            currentStep: SIGNUP_STEPS.COMPLETE
        });
    }
};

export const validateSignupFormData = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    const requiredFields = ['adminEmail'];
    const optionalFields = ['companyName', 'adminFirstName', 'adminLastName', 'adminPhoneNumber'];
    
    // At minimum, we need the email
    if (!requiredFields.every(field => data[field] && data[field].length > 0)) {
        return false;
    }

    // Validate email format if present
    if (data.adminEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.adminEmail)) {
            return false;
        }
    }

    return true;
};

// Smart step detection logic
export const determineCorrectStep = (urlParams = {}) => {
    const { step, codeWasSent } = urlParams;
    const storedData = signupFormStorage.retrieve();
    
    // If user has completed signup, they shouldn't be here
    if (storedData?.currentStep === SIGNUP_STEPS.COMPLETE) {
        return { step: SIGNUP_STEPS.COMPLETE, redirect: '/dashboard' };
    }
    
    // If signup was submitted but user is on initial form, redirect them
    if (signupFormStorage.isSignupSubmitted() && !step) {
        const currentStep = signupFormStorage.getCurrentStep();
        if (currentStep === SIGNUP_STEPS.PASSWORD_SETUP) {
            return { 
                step: SIGNUP_STEPS.PASSWORD_SETUP, 
                redirect: `/signup/set-password/${storedData.adminEmail}/n` 
            };
        } else if (currentStep === SIGNUP_STEPS.CODE_VERIFICATION) {
            return { 
                step: SIGNUP_STEPS.CODE_VERIFICATION, 
                redirect: `/signup/set-password/${storedData.adminEmail}/y` 
            };
        }
    }
    
    // URL-based step detection
    if (step === 'set-password') {
        if (codeWasSent === 'y') {
            return { step: SIGNUP_STEPS.CODE_VERIFICATION };
        } else {
            return { step: SIGNUP_STEPS.PASSWORD_SETUP };
        }
    }
    
    // Default to initial form
    return { step: SIGNUP_STEPS.INITIAL_FORM };
};

// Signup flow URL detection utilities
export const isSignupFlowUrl = (pathname) => {
    if (!pathname) return false;
    
    // Check for various signup URLs
    const signupUrls = [
        '/signup',
        '/signup/',
        '/signup/set-password'
    ];
    
    // Exact matches
    if (signupUrls.includes(pathname)) {
        return true;
    }
    
    // Pattern matches for parametrized URLs like /signup/set-password/email/y
    if (pathname.startsWith('/signup/set-password/')) {
        return true;
    }
    
    return false;
};

export const isPartialAuthState = async () => {
    try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        
        // User is signed in but email might not be verified
        const emailVerified = user?.attributes?.email_verified;
        
        return {
            isSignedIn: true,
            emailVerified: emailVerified === true || emailVerified === 'true',
            isPartialAuth: emailVerified !== true && emailVerified !== 'true'
        };
    } catch (error) {
        // Not signed in at all
        return {
            isSignedIn: false,
            emailVerified: false,
            isPartialAuth: false
        };
    }
};

export const shouldSkipAppAuthentication = (pathname) => {
    // Skip normal App.js authentication logic if:
    // 1. User is on a signup flow URL
    // 2. This prevents conflicts with partial auth states during signup
    return isSignupFlowUrl(pathname);
};